import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, X, Search, Pencil, Trash2, AlertTriangle, Settings as Cog, Copy, Link2, Package, Warehouse } from "lucide-react";
import { toast } from "sonner";
import InventoryCategoryManager from "@/components/inventory/InventoryCategoryManager";
import MasterPartsPanel from "@/components/inventory/MasterPartsPanel";
import MasterPartPicker from "@/components/inventory/MasterPartPicker";

const empty = {
  sku: "",
  name: "",
  barcode: "",
  category: "",
  kind: "product",
  stock: 0,
  min_stock: 1,
  cost_price: 0,
  sale_price: 0,
  vat_rate: 21,
  warranty_months: 0,
  location: "",
  active: true,
  description: "",
  stock_source: "own",
  master_item_id: "",
};

export default function Inventory() {
  const [tab, setTab] = useState("parts"); // "parts" | "masters"
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  // Info del maestro seleccionado en el formulario (para mostrar nombre)
  const [masterSel, setMasterSel] = useState(null);
  // Cuando el backend devuelve 409 (SKU duplicado), guardamos los datos del
  // producto existente para ofrecer "Ir al producto existente".
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  // Iter 73 — SKU y barcode son el mismo identificador. Si al editar un
  // producto legado ambos valores divergen, mostramos un selector para que
  // el usuario elija cuál conservar antes de guardar.
  const [legacyDivergence, setLegacyDivergence] = useState(null);
  // Opciones globales de garantía gestionadas desde Configuración.
  const [warrantyOptions, setWarrantyOptions] = useState([0, 3, 6, 12, 24]);
  const [warrantyDefault, setWarrantyDefault] = useState(3);
  // Iter 71 — categorías gestionables
  const [categories, setCategories] = useState([]);
  const [showCatManager, setShowCatManager] = useState(false);
  const [catFilter, setCatFilter] = useState("");
  const [me, setMe] = useState(null);

  const loadCategories = () => {
    api.get("/settings/inventory-categories")
      .then((r) => setCategories(r.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    api.get("/settings").then((r) => {
      const billing = r.data?.billing || {};
      if (Array.isArray(billing.warranty_options) && billing.warranty_options.length) {
        setWarrantyOptions(billing.warranty_options);
      }
      if (Number.isFinite(Number(billing.warranty_months_default))) {
        setWarrantyDefault(Number(billing.warranty_months_default));
      }
    }).catch(() => { /* keep defaults */ });
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    loadCategories();
  }, []);

  const load = async (queryStr) => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const r = await api.get("/parts", {
        params: { q: queryStr ?? "" },
        signal: ctrl.signal,
      });
      setList(r.data);
    } catch (err) {
      if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
        console.warn("[Inventory] search error:", err);
      }
    }
  };

  // Iter 128 — Debounce + AbortController para evitar race conditions.
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(q.trim()), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line
  }, [q]);

  const startNew = () => {
    setEditing(null);
    setLegacyDivergence(null);
    setMasterSel(null);
    setForm({ ...empty, warranty_months: warrantyDefault });
    setOpen(true);
  };

  const startEdit = (p) => {
    setEditing(p);
    // Iter 73 — Detectar productos legados con SKU/barcode divergentes.
    const skuVal = p.sku || "";
    const barcodeVal = p.barcode || "";
    const divergent = !!skuVal && !!barcodeVal && skuVal !== barcodeVal;
    setLegacyDivergence(divergent ? { sku: skuVal, barcode: barcodeVal } : null);
    // Iter 145 — Cargar info del maestro vinculado si existe.
    if (p.stock_source === "master" && p.master_item_id) {
      setMasterSel({
        id: p.master_item_id,
        sku: p.master_part_sku || "",
        name: p.master_part_name || "",
        // Iter 146 — Garantía heredada precargada.
        warranty_months: p.master_warranty_months ?? 0,
      });
    } else {
      setMasterSel(null);
    }
    setForm({
      sku: skuVal,
      name: p.name,
      // Si los valores difieren, mantenemos ambos en el form temporalmente
      // hasta que el usuario elija. Si coinciden, igualamos por seguridad.
      barcode: divergent ? barcodeVal : skuVal,
      category: p.category || "",
      kind: p.kind || "product",
      stock: p.stock,
      min_stock: p.min_stock,
      cost_price: p.cost_price,
      sale_price: p.sale_price,
      vat_rate: p.vat_rate ?? 21,
      warranty_months: p.warranty_months ?? 0,
      location: p.location || "",
      active: p.active !== false,
      description: p.description || "",
      stock_source: p.stock_source || "own",
      master_item_id: p.master_item_id || "",
    });
    setOpen(true);
  };

  // Iter 116 — Clonar un producto/servicio. Copia todos los datos del origen
  // EXCEPTO SKU/barcode (vacíos, el usuario debe teclear un SKU nuevo). El
  // stock del clon se inicializa a 0. La ficha se abre en modo "Nuevo" —
  // NO se guarda automáticamente hasta que el usuario confirme.
  const clonePart = async (p) => {
    try {
      const r = await api.get(`/parts/${p.id}/clone-payload`);
      const src = r.data || {};
      setEditing(null);
      setLegacyDivergence(null);
      setDuplicateInfo(null);
      setForm({
        sku: "",
        barcode: "",
        name: src.name || "",
        description: src.description || "",
        category: src.category || "",
        kind: src.kind || "product",
        stock: 0,
        min_stock: src.min_stock ?? 1,
        cost_price: src.cost_price ?? 0,
        sale_price: src.sale_price ?? 0,
        vat_rate: src.vat_rate ?? 21,
        warranty_months: src.warranty_months ?? 0,
        location: src.location || "",
        active: src.active !== false,
      });
      setOpen(true);
      toast.info(`Clonando "${p.name}". Introduce un nuevo SKU y guarda.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Iter 145 — Ejecuta la transición de vinculación/desvinculación de un
  // artículo existente con su artículo maestro. Aplica protecciones frente
  // a pérdida/duplicación de stock.
  const applyMasterLinkTransition = async (partId, prevSource, nextSource, prevMasterId, nextMasterId) => {
    // Desvincular
    if (prevSource === "master" && nextSource === "own") {
      const initialRaw = window.prompt(
        "Este artículo dejará de compartir stock con su maestro. Nunca se copia automáticamente.\n\n¿Con cuántas unidades de stock propio quieres que quede el artículo? (0 permitido)",
        "0",
      );
      if (initialRaw === null) return false;
      const initial = parseInt(initialRaw, 10);
      if (!Number.isFinite(initial) || initial < 0) {
        toast.error("Stock inicial inválido.");
        return false;
      }
      await api.post(`/parts/${partId}/unlink-master`, { initial_stock: initial });
      return true;
    }
    // Vincular por primera vez
    if (prevSource === "own" && nextSource === "master") {
      if (!nextMasterId) { toast.error("Selecciona un artículo maestro."); return false; }
      try {
        await api.post(`/parts/${partId}/link-master`, { master_id: nextMasterId });
        return true;
      } catch (err) {
        const st = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (st === 409 && detail && typeof detail === "object" && detail.code === "stock_transfer_required") {
          const opts = `El artículo tiene ${detail.current_stock} unidad(es) de stock propio.\n\n[Aceptar] = Transferir esas unidades al maestro "${detail.master_name}"\n[Cancelar] = Vincular SIN transferir (se descarta el stock propio)`;
          const transfer = window.confirm(opts);
          await api.post(`/parts/${partId}/link-master`, { master_id: nextMasterId, transfer_stock: transfer });
          toast.success(transfer ? "Stock transferido al maestro" : "Vinculado sin transferir");
          return true;
        }
        throw err;
      }
    }
    // Cambio de maestro (master → master diferente): desvincular con stock 0 y volver a vincular
    if (prevSource === "master" && nextSource === "master" && prevMasterId !== nextMasterId) {
      const ok = window.confirm("Vas a cambiar el artículo maestro. El vínculo actual se sustituirá. ¿Continuar?");
      if (!ok) return false;
      await api.post(`/parts/${partId}/unlink-master`, { initial_stock: 0 });
      await api.post(`/parts/${partId}/link-master`, { master_id: nextMasterId });
      return true;
    }
    return true;
  };

  const save = async (e) => {
    e.preventDefault();
    setDuplicateInfo(null);
    // Iter 73 — Bloquear submit si el producto legado todavía tiene SKU y
    // barcode divergentes y el usuario no ha elegido cuál conservar.
    if (legacyDivergence && form.sku !== form.barcode) {
      toast.error("Elige qué valor conservar (SKU o Código de barras) antes de guardar.");
      return;
    }
    // Iter 145 — Validaciones de vinculación con maestro.
    if (form.stock_source === "master" && !masterSel?.id) {
      toast.error("Selecciona un artículo maestro para compartir stock.");
      return;
    }
    const skuFinal = (form.sku || "").trim();
    const payload = {
      ...form,
      sku: skuFinal,
      barcode: skuFinal,
      stock: Number(form.stock) || 0,
      min_stock: Number(form.min_stock) || 0,
      cost_price: Number(form.cost_price) || 0,
      sale_price: Number(form.sale_price) || 0,
      vat_rate: Number(form.vat_rate) || 0,
      warranty_months: Math.max(0, parseInt(form.warranty_months, 10) || 0),
      active: form.active !== false,
      master_item_id: form.stock_source === "master" ? (masterSel?.id || "") : "",
    };
    try {
      if (editing) {
        // Los cambios de vinculación se procesan por endpoints dedicados.
        // Detectamos el estado previo del artículo.
        const prevSource = editing.stock_source || "own";
        const prevMasterId = editing.master_item_id || "";
        const nextSource = form.stock_source || "own";
        const nextMasterId = form.stock_source === "master" ? (masterSel?.id || "") : "";
        // No mandamos stock_source/master_item_id en el PUT (los ignora).
        const { stock_source, master_item_id, ...editPayload } = payload; // eslint-disable-line no-unused-vars
        // Si el artículo está vinculado a master, no mandamos "stock" propio.
        if (nextSource === "master") delete editPayload.stock;
        await api.put(`/parts/${editing.id}`, editPayload);
        // Aplicar transición si cambió el estado de vinculación.
        if (prevSource !== nextSource || prevMasterId !== nextMasterId) {
          const ok = await applyMasterLinkTransition(editing.id, prevSource, nextSource, prevMasterId, nextMasterId);
          if (!ok) { setOpen(false); load(q.trim()); return; }
        }
        toast.success("Repuesto actualizado");
      } else {
        // Nuevo: se puede crear ya vinculado (stock propio ignorado por backend).
        payload.stock_source = form.stock_source || "own";
        await api.post("/parts", payload);
        toast.success("Repuesto creado");
      }
      setOpen(false);
      load(q.trim());
    } catch (err) {
      // SKU duplicado → mostramos UI con botón "Ir al producto existente".
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "duplicate_sku") {
        setDuplicateInfo(detail);
        return;
      }
      toast.error(formatApiError(err));
    }
  };

  const openExisting = async () => {
    if (!duplicateInfo?.existing_id) return;
    try {
      const r = await api.get(`/parts`, { params: { q: duplicateInfo.existing_sku } });
      const found = (r.data || []).find((p) => p.id === duplicateInfo.existing_id);
      if (found) {
        startEdit(found);
        setDuplicateInfo(null);
      } else {
        toast.error("No se pudo cargar el producto existente.");
      }
    } catch {
      toast.error("Error abriendo el producto existente.");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    await api.delete(`/parts/${p.id}`);
    toast.success("Eliminado");
    load(q.trim());
  };

  return (
    <div className="p-6 lg:p-10">
      <header className="flex items-end justify-between pb-6 gap-4 flex-wrap border-b border-dashed border-[#D5D7CE]">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
            <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Almacén
          </div>
          <h1 className="font-display text-4xl lg:text-[38px] font-black mt-2 tracking-tight">Inventario</h1>
        </div>
        {tab === "parts" && (
          <button
            onClick={startNew}
            data-testid="part-new"
            className="ds-btn primary"
          >
            <Plus size={15} /> Nuevo repuesto
          </button>
        )}
      </header>

      {/* Iter 145 — Tabs: Artículos / Artículos maestros */}
      <div className="mt-4 flex items-center gap-1 border-b border-[#E5E5E0]">
        <button
          onClick={() => setTab("parts")}
          data-testid="inv-tab-parts"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            tab === "parts"
              ? "border-[#6E8B3D] text-[#3F5222]"
              : "border-transparent text-[#525252] hover:text-[#111110]"
          }`}
        >
          <Package size={14} className="inline mr-1.5" /> Artículos
        </button>
        <button
          onClick={() => setTab("masters")}
          data-testid="inv-tab-masters"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            tab === "masters"
              ? "border-[#6E8B3D] text-[#3F5222]"
              : "border-transparent text-[#525252] hover:text-[#111110]"
          }`}
        >
          <Warehouse size={14} className="inline mr-1.5" /> Artículos maestros
        </button>
      </div>

      {tab === "masters" ? (
        <div className="mt-4">
          <MasterPartsPanel />
        </div>
      ) : (
      <>
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar SKU, nombre, categoría, código de barras"
            data-testid="part-search"
            className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none pl-9 pr-3 py-2 text-sm rounded-none"
          />
        </div>
        {/* Iter 71 — filtro por categoría */}
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          data-testid="part-filter-category"
          className="ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
        >
          <option value="">Todas las categorías</option>
          {categories.map((c) => (
            <option key={c.id} value={c.label}>
              {c.label}{!c.active ? " (inactiva)" : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4 ds-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b border-[#E5E5E0]">
              <th className="px-4 py-2.5">SKU</th>
              <th className="px-4 py-2.5">Tipo</th>
              <th className="px-4 py-2.5">Nombre</th>
              <th className="px-4 py-2.5">Categoría</th>
              <th className="px-4 py-2.5 text-right">Stock</th>
              <th className="px-4 py-2.5 text-right">Mín.</th>
              <th className="px-4 py-2.5 text-right">Venta</th>
              <th className="px-4 py-2.5 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {list
              .filter((p) => !catFilter || (p.category || "") === catFilter)
              .map((p) => {
              // Iter 145 — Si está vinculado a un maestro, el stock efectivo
              // proviene del maestro y el mínimo también.
              const stockShow = p.kind === "service"
                ? "—"
                : (p.stock_source === "master" ? (p.stock_effective ?? 0) : (p.stock ?? 0));
              const minShow = p.stock_source === "master" ? (p.master_min_stock ?? 0) : (p.min_stock ?? 0);
              const low = p.kind !== "service" && stockShow <= minShow;
              return (
                <tr
                  key={p.id}
                  className={`border-b border-[#E5E5E0] last:border-0 hover:bg-[#F7F7F4]`}
                  data-testid={`part-row-${p.id}`}
                >
                  <td className="px-4 py-2.5 font-mono text-xs">{p.sku}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono uppercase border ${p.kind === "service" ? "bg-[#F0F4E8] text-[#6E8B3D] border-[#6E8B3D]" : "bg-neutral-100 text-neutral-700 border-neutral-300"}`}>
                      {p.kind === "service" ? "Servicio" : "Producto"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-medium">{p.name}</td>
                  <td className="px-4 py-2.5 text-[#525252]">{p.category || "—"}</td>
                  <td className={`px-4 py-2.5 text-right font-mono ${low ? "text-red-700 font-bold" : ""}`}>
                    {p.kind === "service" ? (
                      <span className="text-[#525252]">—</span>
                    ) : (
                      <div className="flex flex-col items-end">
                        <span className="inline-flex items-center gap-1 justify-end">
                          {low && <AlertTriangle size={12} />}
                          {stockShow}
                        </span>
                        {p.stock_source === "master" && p.master_part_sku && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toast.info(`Stock compartido con: ${p.master_part_name}`); }}
                            className="mt-0.5 text-[9px] font-mono uppercase tracking-wider text-[#6E8B3D] hover:text-[#3F5222] flex items-center gap-1"
                            title={`Stock desde: ${p.master_part_name}`}
                            data-testid={`part-master-badge-${p.id}`}
                          >
                            <Link2 size={9} /> {p.master_part_sku}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs">
                    {p.kind === "service" ? "—" : minShow}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs">€{p.sale_price?.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => startEdit(p)} className="p-1.5 hover:bg-[#E5E5E0]" data-testid={`part-edit-${p.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => clonePart(p)}
                      className="p-1.5 hover:bg-[#E5E5E0]"
                      title="Clonar este ítem"
                      data-testid={`part-clone-${p.id}`}
                    >
                      <Copy size={14} />
                    </button>
                    <button onClick={() => del(p)} className="p-1.5 hover:bg-red-100 text-red-700">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-[#525252]">
                  No hay repuestos. Crea el primero.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}

      {open && (
        <Modal size="xl" onClose={() => { setOpen(false); setDuplicateInfo(null); setLegacyDivergence(null); }} title={editing ? "Editar repuesto" : "Nuevo repuesto"}>
          <form onSubmit={save} className="space-y-4">
            {legacyDivergence && form.sku !== form.barcode && (
              <div
                className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm"
                data-testid="part-form-legacy-divergence"
              >
                <div className="font-semibold text-amber-800">
                  Este producto tiene SKU y Código de barras diferentes
                </div>
                <div className="text-xs text-[#525252] mt-1">
                  A partir de ahora ambos campos comparten el mismo identificador.
                  Elige cuál de los dos valores quieres conservar:
                </div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, barcode: form.sku })}
                    data-testid="part-form-keep-sku"
                    className="ds-btn"
                  >
                    Conservar SKU: <span className="font-mono ml-1">{legacyDivergence.sku}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, sku: form.barcode })}
                    data-testid="part-form-keep-barcode"
                    className="ds-btn primary"
                  >
                    Conservar Código de barras: <span className="font-mono ml-1">{legacyDivergence.barcode}</span>
                  </button>
                </div>
              </div>
            )}
            {duplicateInfo && (
              <div
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm"
                data-testid="part-form-duplicate-alert"
              >
                <div className="font-semibold text-red-700">{duplicateInfo.message}</div>
                <div className="text-xs text-[#525252] mt-1 space-y-0.5">
                  <div><span className="font-mono uppercase tracking-widest text-[10px]">Nombre:</span> {duplicateInfo.existing_name}</div>
                  <div><span className="font-mono uppercase tracking-widest text-[10px]">SKU:</span> <span className="font-mono">{duplicateInfo.existing_sku}</span></div>
                  {duplicateInfo.existing_category && (
                    <div><span className="font-mono uppercase tracking-widest text-[10px]">Categoría:</span> {duplicateInfo.existing_category}</div>
                  )}
                  {duplicateInfo.existing_kind && (
                    <div><span className="font-mono uppercase tracking-widest text-[10px]">Tipo:</span> {duplicateInfo.existing_kind === "service" ? "Servicio" : "Producto"}</div>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {duplicateInfo.existing_id && (
                    <button
                      type="button"
                      onClick={openExisting}
                      data-testid="part-form-goto-existing"
                      className="ds-btn primary"
                    >
                      Ir al ítem existente
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setDuplicateInfo(null)}
                    data-testid="part-form-change-sku"
                    className="ds-btn"
                  >
                    Cambiar SKU
                  </button>
                  <button
                    type="button"
                    onClick={() => { setDuplicateInfo(null); setOpen(false); }}
                    data-testid="part-form-cancel-duplicate"
                    className="ds-btn ghost"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Iter 146 — Layout multi-columna: 3 columnas en escritorio,
                2 en tablet, 1 en móvil. Se agrupan las secciones lógicas. */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-4">
              {/* ---------- Columna A: Identificación ---------- */}
              <section className="space-y-3">
                <h3 className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold border-b border-dashed border-[#D5D7CE] pb-1">
                  Identificación
                </h3>
                <div>
                  <Field
                    label="SKU *"
                    required
                    value={form.sku}
                    onChange={(v) => setForm({ ...form, sku: v, barcode: v })}
                    testid="part-form-sku"
                    pattern="[A-Za-z0-9_-]+"
                    title="Solo letras, números, guion (-) y guion bajo (_)"
                  />
                  <div className="mt-1 text-[10px] font-mono text-[#525252] leading-tight">
                    Letras, números, guion y guion bajo. Ej: <span className="font-semibold">BAT-MBA-M1</span>
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Tipo</label>
                  <select
                    value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    data-testid="part-form-kind"
                    className="mt-1 w-full rounded-lg border border-[#E5E5E0] focus:border-[#6E8B3D] focus:ring-2 focus:ring-[#6E8B3D]/20 outline-none px-3 py-2 text-sm bg-white hover:border-[#D5D7CE] transition-colors"
                  >
                    <option value="product">Producto</option>
                    <option value="service">Servicio</option>
                  </select>
                </div>
                <Field label="Nombre *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="part-form-name" />
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Categoría *</label>
                  <div className="mt-1 flex items-stretch gap-2">
                    <select
                      value={form.category || ""}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      required
                      data-testid="part-form-category"
                      className="flex-1 rounded-lg border border-[#E5E5E0] focus:border-[#6E8B3D] focus:ring-2 focus:ring-[#6E8B3D]/20 outline-none px-3 py-2 text-sm bg-white hover:border-[#D5D7CE] transition-colors"
                    >
                      <option value="">Seleccionar categoría</option>
                      {categories.filter((c) => c.active).map((c) => (
                        <option key={c.id} value={c.label}>{c.label}</option>
                      ))}
                      {form.category && !categories.some((c) => c.label === form.category) && (
                        <option value={form.category}>{form.category} (inactiva)</option>
                      )}
                      {form.category && categories.some((c) => c.label === form.category && !c.active) && (
                        <option value={form.category} disabled>
                          {form.category} (inactiva)
                        </option>
                      )}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowCatManager(true)}
                      title="Gestionar categorías"
                      data-testid="part-form-manage-cats"
                      className="ds-btn"
                    >
                      <Cog size={13} />
                    </button>
                  </div>
                </div>
                <Field label="Ubicación" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="part-form-location" />
              </section>

              {/* ---------- Columna B: Precio y garantía ---------- */}
              <section className="space-y-3">
                <h3 className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold border-b border-dashed border-[#D5D7CE] pb-1">
                  Precio y garantía
                </h3>
                <div>
                  <Field label="Precio venta (IVA incluido)" type="number" value={form.sale_price} onChange={(v) => setForm({ ...form, sale_price: v })} testid="part-form-sale" />
                  {(() => {
                    const sp = Number(form.sale_price) || 0;
                    const vr = Number(form.vat_rate) || 0;
                    if (!sp || !vr) return null;
                    const base = sp / (1 + vr / 100);
                    const iva = sp - base;
                    return (
                      <div className="mt-1 text-[10px] font-mono text-[#525252] leading-tight" data-testid="part-form-sale-breakdown">
                        Base sin IVA: €{base.toFixed(2)} · IVA ({vr}%): €{iva.toFixed(2)}
                      </div>
                    );
                  })()}
                </div>
                <Field label="IVA (%)" type="number" value={form.vat_rate} onChange={(v) => setForm({ ...form, vat_rate: v })} />
                {/* Iter 146 — Garantía heredada del maestro cuando corresponde. */}
                <WarrantySelect
                  value={
                    form.stock_source === "master" && masterSel
                      ? (masterSel.warranty_months ?? 0)
                      : form.warranty_months
                  }
                  options={warrantyOptions}
                  onChange={(v) => setForm({ ...form, warranty_months: v })}
                  readOnly={form.stock_source === "master" && !!masterSel}
                  inheritedFromName={form.stock_source === "master" && masterSel ? masterSel.name : ""}
                />
                <Field label="Código de barras" value={form.sku} onChange={() => {}} testid="part-form-barcode" readOnly />
              </section>

              {/* ---------- Columna C: Control de stock ---------- */}
              <section className="space-y-3">
                <h3 className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold border-b border-dashed border-[#D5D7CE] pb-1">
                  Control de stock
                </h3>
                {form.kind === "service" ? (
                  <div className="rounded-lg border border-[#E5E5E0] bg-[#F7F7F4] p-3 text-xs text-[#525252]">
                    Los servicios no consumen stock. Este bloque queda inactivo.
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-2">
                      <label
                        className={`rounded-xl border px-3 py-2.5 text-xs cursor-pointer flex items-start gap-2 transition-all ${
                          form.stock_source === "own"
                            ? "border-[#6E8B3D] bg-[#F0F4E8] shadow-sm"
                            : "border-[#E5E5E0] hover:bg-[#FAFAF6] hover:border-[#D5D7CE]"
                        }`}
                        data-testid="part-form-stock-own"
                      >
                        <input
                          type="radio"
                          name="stock_source"
                          value="own"
                          checked={form.stock_source === "own"}
                          onChange={() => setForm({ ...form, stock_source: "own" })}
                          className="mt-0.5 accent-[#6E8B3D]"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-sm">Stock propio</div>
                          <div className="text-[10px] text-[#525252]">
                            El artículo tiene su propio stock físico.
                          </div>
                        </div>
                      </label>
                      <label
                        className={`rounded-xl border px-3 py-2.5 text-xs cursor-pointer flex items-start gap-2 transition-all ${
                          form.stock_source === "master"
                            ? "border-[#6E8B3D] bg-[#F0F4E8] shadow-sm"
                            : "border-[#E5E5E0] hover:bg-[#FAFAF6] hover:border-[#D5D7CE]"
                        }`}
                        data-testid="part-form-stock-master"
                      >
                        <input
                          type="radio"
                          name="stock_source"
                          value="master"
                          checked={form.stock_source === "master"}
                          onChange={() => setForm({ ...form, stock_source: "master" })}
                          className="mt-0.5 accent-[#6E8B3D]"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-sm flex items-center gap-1">
                            <Link2 size={12} /> Compartir con maestro
                          </div>
                          <div className="text-[10px] text-[#525252]">
                            Consume del stock físico de un artículo maestro.
                          </div>
                        </div>
                      </label>
                    </div>
                    {form.stock_source === "master" && (
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                          Artículo maestro *
                        </label>
                        <div className="mt-1">
                          <MasterPartPicker
                            value={masterSel}
                            onChange={(m) => setMasterSel(m)}
                            testid="part-form-master-picker"
                          />
                        </div>
                      </div>
                    )}
                    {form.stock_source === "own" && (
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Stock" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} testid="part-form-stock" />
                        <Field label="Stock mínimo" type="number" value={form.min_stock} onChange={(v) => setForm({ ...form, min_stock: v })} />
                      </div>
                    )}
                    {form.stock_source === "master" && (
                      <div className="rounded-lg border border-[#E5E5E0] bg-[#F0F4E8] p-2.5 text-[11px] text-[#525252]" data-testid="part-form-stock-linked-info">
                        Stock, mínimo y movimientos se gestionan desde la ficha del maestro
                        {masterSel?.name ? <> <strong>{masterSel.name}</strong>.</> : "."}
                      </div>
                    )}
                  </>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <input
                    id="part-active"
                    type="checkbox"
                    checked={form.active !== false}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="h-4 w-4 accent-[#6E8B3D]"
                    data-testid="part-form-active"
                  />
                  <label htmlFor="part-active" className="text-xs">Activo (disponible en reparaciones)</label>
                </div>
              </section>
            </div>

            {/* Descripción a ancho completo debajo de las 3 columnas */}
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Descripción</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="mt-1 w-full rounded-lg border border-[#E5E5E0] focus:border-[#6E8B3D] focus:ring-2 focus:ring-[#6E8B3D]/20 outline-none px-3 py-2 text-sm bg-white hover:border-[#D5D7CE] transition-colors"
              />
            </div>

            {/* Barra de acciones sticky para mantener siempre visibles Cancelar/Guardar */}
            <div className="sticky bottom-0 -mx-5 -mb-5 px-5 py-3 bg-white border-t border-[#E5E5E0] flex items-center justify-end gap-2 rounded-b-2xl">
              <button
                type="button"
                onClick={() => { setOpen(false); setDuplicateInfo(null); setLegacyDivergence(null); }}
                data-testid="part-form-cancel"
                className="ds-btn"
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="part-form-submit"
                className="ds-btn primary"
              >
                {editing ? "Guardar" : "Crear repuesto"}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {showCatManager && (
        <InventoryCategoryManager
          onClose={() => setShowCatManager(false)}
          onSaved={(newCats) => setCategories(newCats)}
          isAdmin={Array.isArray(me?.roles) ? me.roles.includes("admin") : (me?.role === "admin")}
        />
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, pattern, title, readOnly }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{label}</label>
      <input
        type={type}
        required={required}
        pattern={pattern}
        title={title}
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={`mt-1 w-full rounded-lg border border-[#E5E5E0] focus:border-[#6E8B3D] focus:ring-2 focus:ring-[#6E8B3D]/20 outline-none px-3 py-2 text-sm transition-colors ${readOnly ? "bg-[#F7F7F4] text-[#525252] cursor-not-allowed" : "bg-white hover:border-[#D5D7CE]"}`}
      />
    </div>
  );
}

function Modal({ children, onClose, title, size = "md" }) {
  // Iter 146/147 — Design System v2: bordes suaves, shadow macOS,
  // header limpio con leve gradiente.
  const width = size === "xl" ? "max-w-5xl" : "max-w-md";
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-white rounded-2xl border border-[#E5E5E0] w-full ${width} max-h-[92vh] flex flex-col`}
        style={{ boxShadow: "0 20px 50px rgba(15,18,16,.18), 0 4px 12px rgba(15,18,16,.08)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-5 py-3.5 border-b border-[#E5E5E0] flex-shrink-0 rounded-t-2xl"
          style={{ background: "linear-gradient(180deg,#FDFDFB 0%,#FAFAF6 100%)" }}
        >
          <div className="font-display font-bold text-[15px]">{title}</div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[#F0F0EB] rounded-lg transition-colors"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

/**
 * Desplegable estandarizado de garantía (meses). Soporta valores legacy:
 * si el producto guarda un valor no incluido en las opciones globales, lo
 * añade al desplegable de forma transparente.
 *
 * Iter 146 — Cuando el artículo hereda garantía de un maestro, se muestra
 * en modo solo lectura con la etiqueta "Heredada de: <maestro>".
 */
function WarrantySelect({ value, options, onChange, readOnly = false, inheritedFromName = "" }) {
  const cur = Number(value) || 0;
  const opts = options.includes(cur) ? options : [...options, cur].sort((a, b) => a - b);
  const label = (m) => (m === 0 ? "Sin garantía" : m === 1 ? "1 mes" : `${m} meses`);
  if (readOnly) {
    return (
      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Garantía (meses)
        </label>
        <div
          className="mt-1 w-full rounded-lg border border-[#E5E5E0] bg-[#F7F7F4] text-[#525252] cursor-not-allowed px-3 py-2 text-sm"
          data-testid="part-form-warranty-inherited"
        >
          {label(cur)}
        </div>
        {inheritedFromName && (
          <div className="mt-1 text-[10px] font-mono text-[#525252] leading-tight">
            Heredada de: <span className="font-semibold text-[#3F5222]">{inheritedFromName}</span>
          </div>
        )}
      </div>
    );
  }
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
        Garantía (meses)
      </label>
      <select
        value={cur}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        data-testid="part-form-warranty"
        className="mt-1 w-full rounded-lg border border-[#E5E5E0] focus:border-[#6E8B3D] focus:ring-2 focus:ring-[#6E8B3D]/20 outline-none px-3 py-2 text-sm bg-white hover:border-[#D5D7CE] transition-colors"
      >
        {opts.map((m) => (
          <option key={m} value={m}>{label(m)}</option>
        ))}
      </select>
    </div>
  );
}

